import { MapPin } from "lucide-react";

import { FieldDisplay } from "@/components/public-tracking/FieldDisplay";
import { ScanJourneyMap } from "@/components/public-tracking/ScanJourneyMap";
import {
  formatDateLong,
  formatScanTimestamp,
} from "@/utils/publicTracking/format";
import type { PublicTrackingStatus } from "@/utils/publicTracking/types";
import type { ScanJourney } from "@/utils/publicTracking/useScanJourneyMap";

interface LiveRouteSectionProps {
  data: PublicTrackingStatus;
  journey: ScanJourney;
  delivered: boolean;
}

/**
 * Live-route map + stats section (no card chrome — hosted in the shared
 * two-column detail card).
 */
export function LiveRouteSection({
  data,
  journey,
  delivered,
}: LiveRouteSectionProps) {
  const mapUnavailable = journey.status === "empty" || journey.mapUrl === null;
  const dateValue = delivered
    ? data.delivered_date
    : data.expected_delivery_date;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Live Route</h2>
        <span className="text-muted-foreground text-xs">Estimated path</span>
      </div>

      {mapUnavailable ? (
        <div className="bg-muted/30 text-muted-foreground flex aspect-[560/240] w-full items-center justify-center rounded-md border text-sm">
          <span className="flex flex-col items-center gap-1">
            <MapPin className="size-5" aria-hidden />
            Map unavailable
          </span>
        </div>
      ) : (
        <ScanJourneyMap
          journey={journey}
          showLabels
          showLegend
          estimated={!delivered}
        />
      )}

      <div className="grid grid-cols-2 gap-3 border-t pt-3">
        <FieldDisplay label="Distance remaining">
          {delivered
            ? "Arrived"
            : journey.distanceMiles != null
              ? `~${Math.round(journey.distanceMiles)} mi`
              : "—"}
        </FieldDisplay>
        <FieldDisplay label="Updated">
          {formatScanTimestamp(data.as_of)}
        </FieldDisplay>
      </div>

      <div className="grid grid-cols-2 gap-3 border-t pt-3">
        {data.destination_zip ? (
          <FieldDisplay label="Destination ZIP">
            <span className="text-base font-semibold">
              {data.destination_zip}
            </span>
          </FieldDisplay>
        ) : null}
        <FieldDisplay
          label={delivered ? "Delivered date" : "Expected delivery"}
        >
          <span className="text-base font-semibold">
            {formatDateLong(dateValue) ?? "—"}
          </span>
        </FieldDisplay>
      </div>
    </div>
  );
}
